import { useEffect, useState } from 'react';
import { useEventCallback } from './useEventCallback';
import { useRefWithCurrentValue } from './useRefWithCurrentValue';

export function useResizeObservedElement(
  options: UseResizeObserverOptions
): void {
  const [observeOptions, setObserveOptions] = useState<
    ResizeObserverOptions | undefined
  >(options.observeOptions);
  const observeOptionsRef = useRefWithCurrentValue(observeOptions);

  const callback: ResizeObserverCallback = useEventCallback((...args) => {
    options.callback(...args);
  });

  // prevent new instances of options from creating a new ResizeObserver
  // this enables the user of the hook to just declare the observeOptions inline instead of needing to use `useMemo`
  useEffect(() => {
    if (
      JSON.stringify(options.observeOptions) !==
      JSON.stringify(observeOptionsRef.current)
    ) {
      setObserveOptions(options.observeOptions);
    }
  }, [observeOptionsRef, options.observeOptions]);

  useEffect(() => {
    const observer = new ResizeObserver(callback);

    if (options.element) {
      observer.observe(options.element, observeOptions);
    }

    return () => {
      observer.disconnect();
    };
  }, [options.element, observeOptions, callback]);
}

export type UseResizeObserverOptions = {
  element: Element | null;
  observeOptions?: ResizeObserverOptions;
  callback: ResizeObserverCallback;
};
